/// <amd-module name="VSS/Features/SecurityUI/Roles/RolesManagementService" />
import { IVssService, IVssPageContext, Services, VssService } from "VSS/Platform/Context";

import { RoleAssignment } from "VSS/Features/SecurityUI/Contracts/SecurityRoles";
import { RoleAssignmentsModel } from "VSS/Features/SecurityUI/Models";
import { IRoleManamentPermissions } from "VSS/Features/SecurityUI/ModelContracts";
import { getSecurityRolesClient, ISecurityRolesRestClient } from "VSS/Features/SecurityUI/RestClient/SecurityRoles";

export interface IRolesManagementService extends IVssService {
    /**
     * Gets the role assignment and role definitions using the sepcified arguments and returns a model
     * to be consumed by a component which will contain observables.
     *
     * @param pageContext
     * @param scopeId
     * @param resourceId
     * @param managePermissions
     * @param serviceInstanceType
     * @returns RoleAssignmentModel
     */
    getRoleAssignments(
        pageContext: IVssPageContext,
        scopeId: string,
        resourceId: string,
        managePermissions: IRoleManamentPermissions,
        serviceInstanceType?: string
    ): RoleAssignmentsModel;

    /**
     * Persists all the changes made to the specified role assignments model.
     *
     * @param pageContext
     * @param model
     * @param scopeId
     * @param resourceId
     * @param serviceInstanceType
     */
    saveRoleAssignments(
        pageContext: IVssPageContext,
        model: RoleAssignmentsModel,
        scopeId: string,
        resourceId: string,
        serviceInstanceType?: string
    ): void;

    /**
     * Resets the role assignments to the original ones.
     *
     * @param model
     */
    resetRoleAssignments(model: RoleAssignmentsModel): void;

    /**
     * Sets inheritance on/off for the specified arguments.
     * @param pageContext
     * @param model
     * @param inheritance
     * @param scopeId
     * @param resourceId
     * @param serviceInstanceType
     */
    setInheritance(
        pageContext: IVssPageContext,
        model: RoleAssignmentsModel,
        inheritance: boolean,
        scopeId: string,
        resourceId: string,
        serviceInstanceType?: string
    ): void;
}

class RolesManagementService extends VssService implements IRolesManagementService {
    public getRoleAssignments(
        pageContext: IVssPageContext,
        scopeId: string,
        resourceId: string,
        managePermissions: IRoleManamentPermissions,
        serviceInstanceType?: string
    ): RoleAssignmentsModel {
        const model = new RoleAssignmentsModel();
        this.getRolesClient(pageContext, serviceInstanceType)
            .then(rolesClient => {
                const roleAssignmentsPromise = rolesClient.getRoleAssignments(scopeId, resourceId);
                const roleDefinitionsPromise = rolesClient.getRoleDefinitions(scopeId);
                return Promise.all([roleAssignmentsPromise, roleDefinitionsPromise]);
            })
            .then(result => model.initialize(result[0], result[1], managePermissions))
            .catch(error => model.setError(error));

        return model;
    }

    public saveRoleAssignments(
        pageContext: IVssPageContext,
        model: RoleAssignmentsModel,
        scopeId: string,
        resourceId: string,
        serviceInstanceType?: string
    ): void {
        this.getRolesClient(pageContext, serviceInstanceType)
            .then(rolesClient => {
                // Get necessary updates from model to talk to the server
                const deletedUserIds = model.getDeletedRoleAssignments();
                const changedRoleAssignments = model.getChangedRoleAssignments();

                // Come up with necessary promises, don't go to the server if there is nothing delete or changed
                const deletePromise =
                    deletedUserIds.length > 0
                        ? rolesClient.removeRoleAssignments(deletedUserIds, scopeId, resourceId)
                        : Promise.resolve<void>(undefined);
                const changePromise =
                    changedRoleAssignments.length > 0
                        ? rolesClient.setRoleAssignments(changedRoleAssignments, scopeId, resourceId)
                        : Promise.resolve<RoleAssignment[]>([]);

                return Promise.all([deletePromise, changePromise]).then(() => rolesClient);
            })
            .then(rolesClient => rolesClient.getRoleAssignments(scopeId, resourceId))
            .then(roleAssignments => model.setRoleAssignments(roleAssignments))
            .catch(error => model.setError(error));
    }

    public resetRoleAssignments(model: RoleAssignmentsModel): void {
        model.resetRoleAssignments();
    }

    public setInheritance(
        pageContext: IVssPageContext,
        model: RoleAssignmentsModel,
        inheritPermissions: boolean,
        scopeId: string,
        resourceId: string,
        serviceInstanceType?: string
    ): void {
        this.getRolesClient(pageContext, serviceInstanceType)
            .then(rolesClient => rolesClient.changeInheritance(scopeId, resourceId, inheritPermissions).then(() => rolesClient))
            .then(rolesClient => rolesClient.getRoleAssignments(scopeId, resourceId))
            .then(roleAssignments => model.setRoleAssignments(roleAssignments))
            .catch(error => model.setError(error));
    }

    private getRolesClient(pageContext: IVssPageContext, serviceInstanceType?: string): Promise<ISecurityRolesRestClient> {
        return new Promise((resolve, reject) => {
            try {
                resolve(getSecurityRolesClient(pageContext, { serviceInstanceType }));
            } catch (error) {
                reject(error);
            }
        });
    }
}

const serviceName = "IRolesManagementService";

export function getService(pageContext: IVssPageContext): IRolesManagementService {
    return pageContext.getService<IRolesManagementService>(serviceName);
}

Services.add(serviceName, { serviceFactory: RolesManagementService });
